import systemConfig from '@/config/index.js'
import axios from 'axios'
import store from '@/store/index.js'
import localData from '@/common/localdata.js'
import camelcaseKeys from 'camelcase-keys'
import snakecaseKeys from 'snakecase-keys'
import { redirectToLogin } from '@/common/helper.js'

/**
 * 统一http请求函数
 * 支持Promise
 *
 * @param   {Object}  configExpected  请求对象,至少需要包含url
 * @returns {Object}
 */
const requestApi = (configExpected) => {
  
  // 整理请求相关数据
  const prepareRequest = configExpected => {
    // 输入验证
    if (!configExpected.url) {
      throw new Error('没有指定url无法发起请求')
    }

    // 请求方法
    const method = (configExpected.method && configExpected.method.toLowerCase()) || 'get'

    // 申明config
    const config = {
      url: systemConfig.url.server + configExpected.url,
      method,
      inputToSnakeCase: configExpected.inputToSnakeCase || true,
      inputToSnakeCaseExclude: configExpected.inputToSnakeCaseExclude || [],
      outputToCamelCase: configExpected.outputToCamelCase || true,
      headers: {},
      data: {},
      params: {}
    }

    // 处理请求数据
    if (configExpected.data || configExpected.params) {
      if (['head', 'get', 'options', 'delete'].includes(method)) {
        if (configExpected.params) {
          config.params = configExpected.params
        }
      } else if (['put', 'post', 'patch'].includes(method)) {
        if (configExpected.data) {
          config.data = configExpected.data
        }
        config.headers['Content-Type'] = 'application/json'
      }

      if (config.inputToSnakeCase) {
        config.data = snakecaseKeys(config.data, {
          deep: true,
          exclude: config.inputToSnakeCaseExclude
        })
        config.params = snakecaseKeys(config.params, {
          deep: true,
          exclude: config.inputToSnakeCaseExclude
        })
      }
    }

    // 申明请求凭据相关数据
    const ppsessidKey = 'X-Yf-PPSessid'
    const sessidKey = 'X-Yf-Sessid'
    const ppsessid = store.state.user.ppSessId
    const sessid = store.state.user.sessId

    // 处理请求头
    const publicHeaders = {
      Accept: 'application/json',
      'X-Yf-CID': systemConfig.cid
    }
    if (ppsessid) {
      publicHeaders[ppsessidKey] = ppsessid
    }
    if (sessid) {
      publicHeaders[sessidKey] = sessid
    }
    config.headers = {
      ...publicHeaders,
      ...configExpected.headers
    }

    // 处理debugMode
    if (localData.get('debugMode')) {
      config.params.XDEBUG_SESSION_START = 1
    }

    return new Promise((resolve, reject) => {
      resolve([config, null])
    })
  }

  // axios发起请求
  const request = ([config, response]) => {
    return new Promise((resolve, reject) => {
      axios(Object.assign(config, {
        withCredentials: true     // CORS发送Cookie
      })).then(res => {
        resolve([config, res])
      }).catch(e => {
        reject(e)
      })
    })
  }

  // 处理响应
  const processResponse = ([config, response]) => {
    // 登录状态
    const resLoginStatus = response && response.headers && (response.headers['x-yf-loginstatus'] || response.headers['x-yf-pploginstatus'])
    if (resLoginStatus === '0') {
      localData.set('LoginStatus', 1)
    } else if (resLoginStatus === '1') {
      localData.set('LoginStatus', 2)
    } else if (resLoginStatus === '-1') {
      if (Number(localData.get('LoginStatus')) !== -1) {
        localData.set('LoginStatus', 0)
      }
    }

    // Sessid
    const sessId = response?.headers?.['x-yf-sessid']
    if (sessId) {
      localData.set('SessId', sessId)
    }

    // 转换格式
    if (config && config.outputToCamelCase) {
      response.data = camelcaseKeys(response.data, { deep: true })
    }

    return new Promise((resolve, reject) => {
      resolve([config, response])
    })
  }

  // 处理错误
  const handleError = error => {
    return new Promise((resolve, reject) => {
      if (error.response) {
        processResponse([error.config, error.response]).finally(() => {
          if (error.response.data && error.response.data.msg) {
            error.message = error.response.data.msg
          }
          if (error.response.data.error === 1001) { // 只有1001返回需要登录，其他错误直接提示错误
            redirectToLogin()
          } else {
            reject(new Error(error.message))
          }
        })
      } else if (error.request) {
        error.message = '请求没有响应 ' + error.message
        reject(error)
      } else if (error.message) {
        error.message = '发起请求时出错 ' + error.message
        reject(error)
      } else {
        error.message = '未知错误导致请求失败'
        reject(error)
      }
    })
  }

  // 发起请求并返回结果
  return prepareRequest(configExpected)
    .then(request)
    .then(processResponse)
    .then(([config, response]) => {
      // 成功时只返回数据而不返回整个响应
      return response.data
    }).catch(handleError)
}

export default requestApi